import type { GSplatShaderLanguage } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type {
  CompiledGSplatWorkBufferModifier,
  GSplatWorkBufferModifier,
  GSplatWorkBufferModifierBackend,
  GSplatWorkBufferModifierStage,
} from '@/shared/types/gsplat'

const PLAYCANVAS_WORKBUFFER_HELPERS = Object.freeze([
  'modifySplatCenter',
  'modifySplatRotationScale',
  'modifySplatColor',
] as const)

export class GSplatWorkBufferModifierCompileError extends Error {
  readonly code = 'GSPLAT_WORKBUFFER_MODIFIER_INVALID'
  readonly language: GSplatShaderLanguage
  readonly backend: GSplatWorkBufferModifierBackend
  readonly stage: GSplatWorkBufferModifierStage
  readonly missingHelpers: readonly string[]

  constructor(
    language: GSplatShaderLanguage,
    message: string,
    missingHelpers: readonly string[] = [],
    options: {
      readonly backend?: GSplatWorkBufferModifierBackend
      readonly stage?: GSplatWorkBufferModifierStage
    } = {}
  ) {
    super(message)
    this.name = 'GSplatWorkBufferModifierCompileError'
    this.language = language
    this.backend = options.backend ?? (language === 'wgsl' ? 'webgpu' : 'webgl2')
    this.stage = options.stage ?? 'work-buffer-copy'
    this.missingHelpers = Object.freeze([...missingHelpers])
  }
}

export interface GSplatWorkBufferModifierHashContext {
  readonly backend?: GSplatWorkBufferModifierBackend
  readonly language?: GSplatShaderLanguage
  readonly stage?: GSplatWorkBufferModifierStage
}

/**
 * 对 WorkBuffer modifier 源码生成稳定 hash。
 *
 * 这里使用 FNV-1a 32-bit，和运行时环境无关，适合参与 shader cache key。
 * 未提供 context 时保持旧的 code-only hash；编译器会把 backend/language/stage
 * 纳入 key，避免不同 pipeline 变体复用同一个 hash。
 */
export function hashGSplatWorkBufferModifierCode(
  code: string,
  context?: GSplatWorkBufferModifierHashContext
): number {
  const hasContext =
    context &&
    (context.backend !== undefined || context.language !== undefined || context.stage !== undefined)
  const input = hasContext
    ? [context?.backend ?? '', context?.language ?? '', context?.stage ?? '', code].join('\u0000')
    : code

  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

export function compileGSplatWorkBufferModifier(
  modifier: GSplatWorkBufferModifier | null
): CompiledGSplatWorkBufferModifier | null {
  if (!modifier) return null
  if (!modifier.glsl) {
    throw new GSplatWorkBufferModifierCompileError('glsl', 'WorkBuffer modifier 缺少 GLSL 源码')
  }

  return {
    code: modifier.glsl,
    hash: hashGSplatWorkBufferModifierCode(modifier.glsl, {
      backend: 'webgl2',
      language: 'glsl',
      stage: 'work-buffer-copy',
    }),
    backend: 'webgl2',
    language: 'glsl',
    stage: 'work-buffer-copy',
  }
}

/**
 * 新 Unified binding 使用的严格 PlayCanvas modifier 编译边界。
 *
 * 这里仅选择当前 backend 的源语言并校验公开 helper 契约；shader/pipeline 的真实编译仍由
 * 引用 Placement 的 Manager 事务完成。
 */
export function compileGSplatWorkBufferModifierForLanguage(
  modifier: GSplatWorkBufferModifier | null,
  language: GSplatShaderLanguage,
  options: {
    readonly backend?: GSplatWorkBufferModifierBackend
    readonly stage?: GSplatWorkBufferModifierStage
  } = {}
): CompiledGSplatWorkBufferModifier | null {
  if (!modifier) return null
  const backend = options.backend ?? (language === 'wgsl' ? 'webgpu' : 'webgl2')
  const stage = options.stage ?? 'work-buffer-copy'
  const code = modifier[language]
  if (!code?.trim()) {
    throw new GSplatWorkBufferModifierCompileError(
      language,
      `WorkBuffer modifier 缺少当前 backend 所需的 ${language.toUpperCase()} 源码`,
      [],
      { backend, stage }
    )
  }

  const missingHelpers = PLAYCANVAS_WORKBUFFER_HELPERS.filter(
    (helper) => !new RegExp(`\\b${helper}\\s*\\(`).test(code)
  )
  if (missingHelpers.length > 0) {
    throw new GSplatWorkBufferModifierCompileError(
      language,
      `WorkBuffer modifier 缺少 PlayCanvas helper: ${missingHelpers.join(', ')}`,
      missingHelpers,
      { backend, stage }
    )
  }

  return Object.freeze({
    code,
    hash: hashGSplatWorkBufferModifierCode(code, { backend, language, stage }),
    backend,
    language,
    stage,
  })
}
